/**
 * @fileoverview Phone Number Module Validation Utilities
 * 
 * This file contains validation functions for phone number module configurations,
 * search criteria, and other module-specific data validation.
 */

import { PhoneNumberContext, PurchaseFlow } from '../types.js';

/**
 * Validate phone number module configuration
 * 
 * @param {PhoneNumberConfig} config - Configuration to validate
 * @returns {Object} Validation result with valid flag and errors array
 */
export function validatePhoneNumberConfig(config) {
  const result = {
    valid: true,
    errors: []
  };

  // Check if config exists
  if (!config || typeof config !== 'object') {
    result.valid = false;
    result.errors.push('Configuration must be a valid object');
    return result;
  }

  // Validate context
  if (!config.context) {
    result.valid = false;
    result.errors.push('Context is required');
  } else if (!Object.values(PhoneNumberContext).includes(config.context)) {
    result.valid = false;
    result.errors.push(`Invalid context: ${config.context}. Must be one of: ${Object.values(PhoneNumberContext).join(', ')}`);
  }

  // Validate purchase flow
  if (config.purchaseFlow && !Object.values(PurchaseFlow).includes(config.purchaseFlow)) {
    result.valid = false;
    result.errors.push(`Invalid purchase flow: ${config.purchaseFlow}. Must be one of: ${Object.values(PurchaseFlow).join(', ')}`);
  }

  // Validate search filters
  if (config.searchFilters) {
    if (!Array.isArray(config.searchFilters)) {
      result.valid = false;
      result.errors.push('Search filters must be an array');
    } else {
      config.searchFilters.forEach((filter, index) => {
        const filterValidation = validateSearchFilter(filter);
        if (!filterValidation.valid) {
          result.valid = false;
          result.errors.push(`Search filter ${index}: ${filterValidation.errors.join(', ')}`);
        }
      });
    }
  }

  // Validate callback functions
  if (config.onNumberSelected && typeof config.onNumberSelected !== 'function') {
    result.valid = false;
    result.errors.push('onNumberSelected must be a function');
  }

  if (config.onPurchaseComplete && typeof config.onPurchaseComplete !== 'function') {
    result.valid = false;
    result.errors.push('onPurchaseComplete must be a function');
  }

  // Validate boolean flags
  if (config.showSkipOption !== undefined && typeof config.showSkipOption !== 'boolean') {
    result.valid = false;
    result.errors.push('showSkipOption must be a boolean');
  }

  if (config.allowBatchPurchase !== undefined && typeof config.allowBatchPurchase !== 'boolean') {
    result.valid = false;
    result.errors.push('allowBatchPurchase must be a boolean');
  }

  if (config.enableAdvancedSearch !== undefined && typeof config.enableAdvancedSearch !== 'boolean') {
    result.valid = false;
    result.errors.push('enableAdvancedSearch must be a boolean');
  }

  // Validate numeric values
  if (config.maxResults !== undefined) {
    if (typeof config.maxResults !== 'number' || config.maxResults < 1 || config.maxResults > 100) {
      result.valid = false;
      result.errors.push('maxResults must be a number between 1 and 100');
    }
  }

  // Validate security level
  if (config.securityLevel && !['user', 'admin'].includes(config.securityLevel)) {
    result.valid = false;
    result.errors.push('securityLevel must be either "user" or "admin"');
  }

  // Context-specific validations
  if (config.context === PhoneNumberContext.ONBOARDING) {
    if (config.allowBatchPurchase) {
      result.valid = false;
      result.errors.push('Batch purchase is not allowed in onboarding context');
    }
  }

  if (config.context === PhoneNumberContext.ADMIN) {
    if (config.securityLevel !== 'admin') {
      result.valid = false;
      result.errors.push('Admin context requires admin security level');
    }
  }

  return result;
}

/**
 * Validate search filter configuration
 * 
 * @param {SearchFilter} filter - Search filter to validate
 * @returns {Object} Validation result
 */
export function validateSearchFilter(filter) {
  const result = {
    valid: true,
    errors: []
  };

  if (!filter || typeof filter !== 'object') {
    result.valid = false;
    result.errors.push('Filter must be a valid object');
    return result;
  }

  // Validate filter type
  const validTypes = ['country', 'area-code', 'city', 'region', 'pattern', 'postal-code', 'exchange'];
  if (!filter.type || !validTypes.includes(filter.type)) {
    result.valid = false;
    result.errors.push(`Invalid filter type: ${filter.type}. Must be one of: ${validTypes.join(', ')}`);
  }

  // Validate enabled flag
  if (typeof filter.enabled !== 'boolean') {
    result.valid = false;
    result.errors.push('Filter enabled flag must be a boolean');
  }

  // Validate required flag
  if (filter.required !== undefined && typeof filter.required !== 'boolean') {
    result.valid = false;
    result.errors.push('Filter required flag must be a boolean');
  }

  // Validate validation rules
  if (filter.validation) {
    if (!Array.isArray(filter.validation)) {
      result.valid = false;
      result.errors.push('Filter validation must be an array');
    } else {
      filter.validation.forEach((rule, index) => {
        const ruleValidation = validateValidationRule(rule);
        if (!ruleValidation.valid) {
          result.valid = false;
          result.errors.push(`Validation rule ${index}: ${ruleValidation.errors.join(', ')}`);
        }
      });
    }
  }

  return result;
}

/**
 * Validate validation rule
 * 
 * @param {ValidationRule} rule - Validation rule to validate
 * @returns {Object} Validation result
 */
export function validateValidationRule(rule) {
  const result = {
    valid: true,
    errors: []
  };

  if (!rule || typeof rule !== 'object') {
    result.valid = false;
    result.errors.push('Validation rule must be a valid object');
    return result;
  }

  // Validate rule type
  const validTypes = ['required', 'minLength', 'maxLength', 'pattern', 'custom'];
  if (!rule.type || !validTypes.includes(rule.type)) {
    result.valid = false;
    result.errors.push(`Invalid rule type: ${rule.type}. Must be one of: ${validTypes.join(', ')}`);
  }

  // Validate message
  if (!rule.message || typeof rule.message !== 'string') {
    result.valid = false;
    result.errors.push('Validation rule must have a string message');
  }

  // Type-specific validations
  switch (rule.type) {
    case 'minLength':
    case 'maxLength':
      if (typeof rule.value !== 'number' || rule.value < 0) {
        result.valid = false;
        result.errors.push(`${rule.type} value must be a non-negative number`);
      }
      break;

    case 'pattern':
      if (typeof rule.value !== 'string') {
        result.valid = false;
        result.errors.push('Pattern value must be a string');
      } else {
        try {
          new RegExp(rule.value);
        } catch (e) {
          result.valid = false;
          result.errors.push('Pattern value must be a valid regular expression');
        }
      }
      break;

    case 'custom':
      if (typeof rule.validator !== 'function') {
        result.valid = false;
        result.errors.push('Custom validation rule must have a validator function');
      }
      break;
  }

  return result;
}

/**
 * Validate search criteria
 * 
 * @param {SearchCriteria} criteria - Search criteria to validate
 * @param {SearchFilter[]} filters - Available filters for validation
 * @returns {Object} Validation result
 */
export function validateSearchCriteria(criteria, filters = []) {
  const result = {
    valid: true,
    errors: []
  };

  if (!criteria || typeof criteria !== 'object') {
    result.valid = false;
    result.errors.push('Search criteria must be a valid object');
    return result;
  }

  // Check required filters
  const requiredFilters = filters.filter(filter => filter.required);
  requiredFilters.forEach(filter => {
    const value = criteria[filter.type];
    if (!value || !value.toString().trim()) {
      result.valid = false;
      result.errors.push(`${filter.label || filter.type} is required`);
    }
  });

  // Validate individual criteria values
  Object.entries(criteria).forEach(([key, value]) => {
    if (value && value.toString().trim()) {
      const filter = filters.find(f => f.type === key);
      if (filter && filter.validation) {
        const validationResult = validateCriteriaValue(value, filter.validation);
        if (!validationResult.valid) {
          result.valid = false;
          result.errors.push(...validationResult.errors.map(error => 
            `${filter.label || key}: ${error}`
          ));
        }
      }
    }
  });

  // Specific validations
  if (criteria.areaCode && !/^\d{3}$/.test(criteria.areaCode)) {
    result.valid = false;
    result.errors.push('Area code must be exactly 3 digits');
  }

  if (criteria.phoneExchange && !/^\d{3}$/.test(criteria.phoneExchange)) {
    result.valid = false;
    result.errors.push('Phone exchange must be exactly 3 digits');
  }

  if (criteria.postalCode && criteria.countryCode === 'US' && !/^\d{5}(-\d{4})?$/.test(criteria.postalCode)) {
    result.valid = false;
    result.errors.push('US postal code must be in format 12345 or 12345-6789');
  }

  if (criteria.letterPattern && !/^[A-Z]+$/.test(criteria.letterPattern)) {
    result.valid = false;
    result.errors.push('Letter pattern must contain only uppercase letters');
  }

  if (criteria.numberPattern && !/^\d+$/.test(criteria.numberPattern)) {
    result.valid = false;
    result.errors.push('Number pattern must contain only digits');
  }

  return result;
}

/**
 * Validate criteria value against validation rules
 * 
 * @param {*} value - Value to validate
 * @param {ValidationRule[]} rules - Validation rules to apply
 * @returns {Object} Validation result
 */
export function validateCriteriaValue(value, rules) {
  const result = {
    valid: true,
    errors: []
  };

  const stringValue = value ? value.toString() : '';

  rules.forEach(rule => {
    switch (rule.type) {
      case 'required':
        if (!stringValue.trim()) {
          result.valid = false;
          result.errors.push(rule.message);
        }
        break;

      case 'minLength':
        if (stringValue.length < rule.value) {
          result.valid = false;
          result.errors.push(rule.message);
        }
        break;

      case 'maxLength':
        if (stringValue.length > rule.value) {
          result.valid = false;
          result.errors.push(rule.message);
        }
        break;

      case 'pattern':
        const regex = new RegExp(rule.value);
        if (!regex.test(stringValue)) {
          result.valid = false;
          result.errors.push(rule.message);
        }
        break;

      case 'custom':
        if (rule.validator && !rule.validator(value)) {
          result.valid = false;
          result.errors.push(rule.message);
        }
        break;
    }
  });

  return result;
}

/**
 * Validate phone number format
 * 
 * @param {string} phoneNumber - Phone number to validate
 * @returns {Object} Validation result
 */
export function validatePhoneNumber(phoneNumber) {
  const result = {
    valid: true,
    errors: []
  };

  if (!phoneNumber || typeof phoneNumber !== 'string') {
    result.valid = false;
    result.errors.push('Phone number must be a string');
    return result;
  }

  // Check E.164 format
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  if (!e164Regex.test(phoneNumber)) {
    result.valid = false;
    result.errors.push('Phone number must be in E.164 format (e.g., +1234567890)');
  }

  return result;
}

/**
 * Validate purchase request
 * 
 * @param {PurchaseRequest} request - Purchase request to validate
 * @returns {Object} Validation result
 */
export function validatePurchaseRequest(request) {
  const result = {
    valid: true,
    errors: []
  };

  if (!request || typeof request !== 'object') {
    result.valid = false;
    result.errors.push('Purchase request must be a valid object');
    return result;
  }

  // Validate phone number
  const phoneValidation = validatePhoneNumber(request.phoneNumber);
  if (!phoneValidation.valid) {
    result.valid = false;
    result.errors.push(...phoneValidation.errors);
  }

  // Validate user ID if provided (admin context)
  if (request.userId && typeof request.userId !== 'string') {
    result.valid = false;
    result.errors.push('User ID must be a string');
  }

  return result;
}
